import React, {
  createContext,
  PropsWithChildren,
  RefObject,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native';
import {
  ChatComponentActionsType,
  ChatComponentType,
} from '@common/hooks/useChatComponentActions';
import { ChatComponentProps } from './type';
import { profileSelector } from '@common/redux/selectors/auth';
import { useAppSelector } from '@common/redux/hooks';
import { UploadFileActionType } from '@common/hooks/useUploadFile';
import { CHAT_DUMMY_DATA } from '../../ChatComponent/utils';

type VoidFunctionWithData<T> = (data: T) => void;
export type ChatComponentState = {
  groupLoading: boolean;
  setGroupLoading: React.Dispatch<React.SetStateAction<boolean>>;

  messageHistoryLoading: boolean;
  setMessageHistoryLoading: React.Dispatch<React.SetStateAction<boolean>>;

  messageSending: boolean;
  setMessageSending: React.Dispatch<React.SetStateAction<boolean>>;

  selectedGroup: ChatComponentType.GroupMessageTypeWithMessageJson | null;
  setSelectedGroup: (groupId: string | null) => void;

  groupMessages: ChatComponentType.GroupMessageTypeWithMessageJson[];
  setGroupMessages: VoidFunctionWithData<
    ChatComponentType.GroupMessageTypeWithMessageJson[]
  >;

  unreadMessageStatus: Record<string, number>;
  setUnreadMessageStatus: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;

  messageHistories: ChatComponentType.ChatMessagesTypeWithMessageJson[];
  setMessageHistories: React.Dispatch<
    React.SetStateAction<ChatComponentType.ChatMessagesTypeWithMessageJson[]>
  >;

  messageHistoriesPagination: Pick<
    ChatComponentType.PaginationResponse,
    'limit' | 'skip' | 'total'
  >;
  setMessageHistoriesPagination: React.Dispatch<
    React.SetStateAction<
      Pick<ChatComponentType.PaginationResponse, 'limit' | 'skip' | 'total'>
    >
  >;

  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  filesValue: ChatComponentType.FileType[];
  setFilesValue: React.Dispatch<
    React.SetStateAction<ChatComponentType.FileType[]>
  >;

  selectedImage: ChatComponentType.FileType | null;
  setSelectedImage: React.Dispatch<
    React.SetStateAction<ChatComponentType.FileType | null>
  >;

  listRef:
    | RefObject<FlatList<ChatComponentType.ChatMessagesTypeWithMessageJson>>
    | undefined;

  isOwnMessage: (data: { senderId: string }) => boolean;

  onDownloadFile: ChatComponentActionsType['onDownloadFile'];
  uploadFile: UploadFileActionType['uploadFile'];
  selectFile: UploadFileActionType['selectFile'];
  fileUploading: UploadFileActionType['uploading'];
  fetchLinkPreview: ChatComponentActionsType['fetchLinkPreview'];
  listScrollPosition: { x: number; y: number };
  handleScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;

  props: ChatComponentProps;
};

export const ChatComponentContext = createContext<ChatComponentState>(
  {} as any
);
export const useChatComponentContext = () => useContext(ChatComponentContext);

export const ChatComponentProvider: React.FC<
  PropsWithChildren<ChatComponentProps>
> = ({ children, ...props }) => {
  const profile = useAppSelector(profileSelector);
  const [listScrollPosition, setListScrollPosition] = useState({ x: 0, y: 0 });
  const [groupLoading, setGroupLoading] = useState(false);
  const [messageHistoryLoading, setMessageHistoryLoading] = useState(false);
  const [messageSending, setMessageSending] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [filesValue, setFilesValue] = useState<ChatComponentType.FileType[]>(
    []
  );
  const [selectedImage, setSelectedImage] =
    useState<ChatComponentType.FileType | null>(null);
  const [selectedGroup, _setSelectedGroup] =
    useState<ChatComponentType.GroupMessageTypeWithMessageJson | null>(null);
  const [groupMessages, _setGroupMessages] = useState<
    ChatComponentType.GroupMessageTypeWithMessageJson[]
  >([]);
  const [unreadMessageStatus, setUnreadMessageStatus] = useState<
    Record<string, number>
  >({});
  const [messageHistories, setMessageHistories] = useState<
    ChatComponentType.ChatMessagesTypeWithMessageJson[]
  >([]);
  const [messageHistoriesPagination, setMessageHistoriesPagination] = useState<
    Pick<ChatComponentType.PaginationResponse, 'limit' | 'skip' | 'total'>
  >({ limit: 10, skip: 0, total: 0 });
  const listRef = useRef<FlatList>(null);

  const setSelectedGroup = (groupId: string | null) => {
    if (!groupId) {
      _setSelectedGroup(null);
      return;
    }
    const groupSelect = groupMessages.find((g) => g.id === groupId);
    if (groupSelect) {
      _setSelectedGroup(groupSelect);
      return;
    }
    _setSelectedGroup(null);
  };
  const setGroupMessages = (
    data: ChatComponentType.GroupMessageTypeWithMessageJson[]
  ) => {
    const dataSorted = data.sort((a, b) => {
      const aDate = a.latestMessage?.createdAt;
      const bDate = b.latestMessage?.createdAt;
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      return new Date(bDate).getTime() - new Date(aDate).getTime();
    });
    _setGroupMessages((old) => {
      if (!old.length) {
        return dataSorted;
      }
      for (const oldItem of old) {
        const itemMatch = dataSorted.find((e) => e.id === oldItem.id);
        if (!itemMatch) {
          return dataSorted;
        }
        // update when new user
        if (itemMatch.numberOfUsers !== oldItem.numberOfUsers) {
          return dataSorted;
        }
        // update when new message is created
        if (itemMatch.latestMessage?.id !== oldItem.latestMessage?.id) {
          return dataSorted;
        }
        // MIK-4828: update when the chatroom's "disable file upload" flag is
        // toggled, otherwise the composer keeps the value it had when the room
        // was opened.
        if (itemMatch.uploadFileDisabled !== oldItem.uploadFileDisabled) {
          return dataSorted;
        }
      }
      return old;
    });
  };
  const isOwnMessage = useCallback(
    ({ senderId }: { senderId: string }) => {
      return senderId === profile?.id;
    },
    [profile?.id]
  );
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      setListScrollPosition({
        x: event.nativeEvent.contentOffset.x,
        y: event.nativeEvent.contentOffset.y,
      });
    },
    []
  );
  return (
    <ChatComponentContext.Provider
      value={{
        groupLoading,
        setGroupLoading,
        messageHistoryLoading,
        setMessageHistoryLoading,
        selectedGroup,
        setSelectedGroup,
        groupMessages,
        setGroupMessages,
        unreadMessageStatus,
        setUnreadMessageStatus,
        messageHistories,
        setMessageHistories,
        messageSending,
        setMessageSending,
        inputValue,
        setInputValue,
        filesValue,
        setFilesValue,
        selectedImage,
        setSelectedImage,
        messageHistoriesPagination,
        setMessageHistoriesPagination,
        listRef,
        isOwnMessage,
        onDownloadFile: props.chatActions.onDownloadFile,
        selectFile: props.fileActions.selectFile,
        uploadFile: props.fileActions.uploadFile,
        fetchLinkPreview: props.chatActions.fetchLinkPreview,
        fileUploading: props.fileActions.uploading,
        props,
        listScrollPosition,
        handleScroll,
      }}
    >
      {children}
    </ChatComponentContext.Provider>
  );
};

export const CanvasChatComponentProvider: React.FC<
  PropsWithChildren<ChatComponentProps>
> = ({ children, ...props }) => {
  const voidFunction = (...args: any[]) => {};
  const isOwnMessage = ({ senderId }: { senderId: string }) => {
    return senderId === '1';
  };
  return (
    <ChatComponentContext.Provider
      value={{
        groupLoading: false,
        setGroupLoading: voidFunction,
        messageHistoryLoading: false,
        setMessageHistoryLoading: voidFunction,
        selectedGroup: {
          id: '1',
          numberOfUsers: 2,
          groupName: 'ユーザー名',
          latestMessage: null,
        },
        setSelectedGroup: voidFunction,
        groupMessages: CHAT_DUMMY_DATA.ChatGroup,
        setGroupMessages: voidFunction,
        unreadMessageStatus: CHAT_DUMMY_DATA.UnReadGroupStatus,
        setUnreadMessageStatus: voidFunction,
        messageHistories: CHAT_DUMMY_DATA.ChatMessages,
        setMessageHistories: voidFunction,
        messageSending: false,
        setMessageSending: voidFunction,
        inputValue: '',
        setInputValue: voidFunction,
        filesValue: [],
        setFilesValue: voidFunction,
        selectedImage: null,
        setSelectedImage: voidFunction,
        messageHistoriesPagination: { limit: 0, skip: 0, total: 0 },
        setMessageHistoriesPagination: voidFunction,
        listRef: undefined,
        isOwnMessage,
        onDownloadFile: voidFunction as any,
        selectFile: voidFunction as any,
        uploadFile: voidFunction as any,
        fetchLinkPreview: voidFunction as any,
        fileUploading: false,
        props,
        listScrollPosition: { x: 0, y: 0 },
        handleScroll: voidFunction as any,
      }}
    >
      {children}
    </ChatComponentContext.Provider>
  );
};
